import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  Download,
  Loader2,
  Palette,
  PenTool,
  Save,
  Sparkles,
  Type,
  Wand2,
  RefreshCw,
  Eye,
} from 'lucide-react';
import Button from '../../components/Button';
import { downloadService } from '../../services/downloadService';
import { brandCraftService, BrandCraftInput, LogoVariation, BrandMockup } from '../../services/brandCraftService';
import { API_BASE_URL } from '../../utils/apiUrl';

/* ─── Constants ─── */

const STEPS = [
  { id: 'identity', label: 'Brand Identity', icon: <PenTool className="h-4 w-4" /> },
  { id: 'colors', label: 'Color Palette', icon: <Palette className="h-4 w-4" /> },
  { id: 'fonts', label: 'Typography', icon: <Type className="h-4 w-4" /> },
  { id: 'logos', label: 'Logo Generation', icon: <Sparkles className="h-4 w-4" /> },
  { id: 'preview', label: 'Brand Preview', icon: <Eye className="h-4 w-4" /> },
];

const COLOR_PALETTES = [
  { name: 'Royal Purple', primary: '#6C3CE1', secondary: '#1E1B4B', accent: '#F59E0B', category: 'Premium' },
  { name: 'Ocean Blue', primary: '#0EA5E9', secondary: '#0F172A', accent: '#22D3EE', category: 'Tech' },
  { name: 'Forest Green', primary: '#16A34A', secondary: '#14532D', accent: '#FDE047', category: 'Nature' },
  { name: 'Sunset Orange', primary: '#F97316', secondary: '#431407', accent: '#FBBF24', category: 'Energy' },
  { name: 'Rose Pink', primary: '#EC4899', secondary: '#4A044E', accent: '#F9A8D4', category: 'Beauty' },
  { name: 'Midnight Dark', primary: '#8B5CF6', secondary: '#0F0F23', accent: '#06B6D4', category: 'Modern' },
  { name: 'Earth Tones', primary: '#92400E', secondary: '#451A03', accent: '#D97706', category: 'Artisan' },
  { name: 'Coral Reef', primary: '#F43F5E', secondary: '#1C1917', accent: '#FB923C', category: 'Lifestyle' },
  { name: 'Steel Gray', primary: '#475569', secondary: '#0F172A', accent: '#3B82F6', category: 'Corporate' },
  { name: 'Emerald Gold', primary: '#059669', secondary: '#064E3B', accent: '#FBBF24', category: 'Finance' },
  { name: 'Neon Cyber', primary: '#A855F7', secondary: '#09090B', accent: '#22D3EE', category: 'Gaming' },
  { name: 'Warm Sand', primary: '#D97706', secondary: '#292524', accent: '#EF4444', category: 'Food' },
];

const FONT_PAIRINGS = [
  { heading: 'Inter', body: 'Inter', label: 'Clean & Modern', category: 'Sans-serif' },
  { heading: 'Playfair Display', body: 'Source Sans 3', label: 'Elegant Editorial', category: 'Serif + Sans' },
  { heading: 'Montserrat', body: 'Open Sans', label: 'Bold & Friendly', category: 'Sans-serif' },
  { heading: 'Poppins', body: 'Nunito', label: 'Rounded & Warm', category: 'Sans-serif' },
  { heading: 'Lora', body: 'Roboto', label: 'Classic & Readable', category: 'Serif + Sans' },
  { heading: 'Bebas Neue', body: 'Inter', label: 'Impact & Contrast', category: 'Display + Sans' },
  { heading: 'DM Serif Display', body: 'DM Sans', label: 'Sophisticated', category: 'Serif + Sans' },
  { heading: 'Space Grotesk', body: 'Space Grotesk', label: 'Tech Forward', category: 'Sans-serif' },
  { heading: 'Outfit', body: 'Outfit', label: 'Geometric Clean', category: 'Sans-serif' },
  { heading: 'Abril Fatface', body: 'Lato', label: 'Bold Headlines', category: 'Display + Sans' },
  { heading: 'Sora', body: 'IBM Plex Sans', label: 'Minimalist Pro', category: 'Sans-serif' },
  { heading: 'Cormorant Garamond', body: 'Proza Libre', label: 'Luxury Serif', category: 'Serif + Sans' },
];

/* ─── Component ─── */

const BrandCraft: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);

  // Step 1: Identity
  const [brandName, setBrandName] = useState('');
  const [tagline, setTagline] = useState('');
  const [description, setDescription] = useState('');

  // Step 2: Colors
  const [selectedPaletteIdx, setSelectedPaletteIdx] = useState<number | null>(null);
  const [primaryColor, setPrimaryColor] = useState('#6C3CE1');
  const [secondaryColor, setSecondaryColor] = useState('#1E1B4B');
  const [accentColor, setAccentColor] = useState('#F59E0B');

  // Step 3: Fonts
  const [selectedFontIdx, setSelectedFontIdx] = useState<number | null>(null);
  const [fontHeading, setFontHeading] = useState('Inter');
  const [fontBody, setFontBody] = useState('Inter');
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Step 4: Logos
  const [logos, setLogos] = useState<LogoVariation[]>([]);
  const [selectedLogoIdx, setSelectedLogoIdx] = useState<number | null>(null);
  const [generatingLogos, setGeneratingLogos] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);

  // Step 5: Preview / Mockups
  const [mockups, setMockups] = useState<BrandMockup[]>([]);
  const [generatingMockups, setGeneratingMockups] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load Google Fonts dynamically
  useEffect(() => {
    const uniqueFonts = Array.from(new Set(FONT_PAIRINGS.flatMap(p => [p.heading, p.body])));
    const families = uniqueFonts.map(f => `family=${f.replace(/\s+/g, '+')}:wght@400;600;700`).join('&');
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    link.rel = 'stylesheet';
    link.onload = () => setFontsLoaded(true);
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  const buildInput = useCallback((): BrandCraftInput => ({
    brandName, tagline, description, primaryColor, secondaryColor, accentColor, fontHeading, fontBody,
  }), [brandName, tagline, description, primaryColor, secondaryColor, accentColor, fontHeading, fontBody]);

  const handleSelectPalette = (idx: number) => {
    setSelectedPaletteIdx(idx);
    const p = COLOR_PALETTES[idx];
    setPrimaryColor(p.primary);
    setSecondaryColor(p.secondary);
    setAccentColor(p.accent);
  };

  const handleSelectFont = (idx: number) => {
    setSelectedFontIdx(idx);
    setFontHeading(FONT_PAIRINGS[idx].heading);
    setFontBody(FONT_PAIRINGS[idx].body);
  };

  const handleGenerateLogos = async () => {
    setGeneratingLogos(true);
    setLogoError(null);
    setLogos([]);
    setSelectedLogoIdx(null);
    try {
      const result = await brandCraftService.generateLogos(buildInput());
      setLogos(result);
    } catch (err: any) {
      setLogoError(err.response?.data?.error?.message || err.message || 'Failed to generate logos');
    } finally {
      setGeneratingLogos(false);
    }
  };

  const handleGenerateMockups = async () => {
    if (selectedLogoIdx === null) return;
    setGeneratingMockups(true);
    try {
      const result = await brandCraftService.generateMockups({
        ...buildInput(),
        logoUrl: logos[selectedLogoIdx].url,
      });
      setMockups(result);
    } catch (err: any) {
      console.error('Mockup generation error:', err);
    } finally {
      setGeneratingMockups(false);
    }
  };

  const handleSaveBrand = async () => {
    if (selectedLogoIdx === null) return;
    setSaving(true);
    try {
      await brandCraftService.saveBrand({
        brandName,
        tagline,
        primaryColor,
        secondaryColor,
        accentColor,
        logoUrl: logos[selectedLogoIdx].url,
      });
      setSaved(true);
    } catch (err: any) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const canProceed = (): boolean => {
    if (currentStep === 0) return brandName.trim().length >= 2;
    if (currentStep === 1) return true;
    if (currentStep === 2) return selectedFontIdx !== null;
    if (currentStep === 3) return selectedLogoIdx !== null;
    return true;
  };

  const goNext = () => {
    if (currentStep === 3 && logos.length === 0) {
      handleGenerateLogos();
      return;
    }
    if (currentStep === 3 && selectedLogoIdx !== null && mockups.length === 0) {
      setCurrentStep(4);
      handleGenerateMockups();
      return;
    }
    if (currentStep < STEPS.length - 1) setCurrentStep(currentStep + 1);
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const getAssetUrl = (url: string) => url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  const handleDownloadBrandMockup = async (mockup: BrandMockup) => {
    try {
      const filename = `${mockup.templateName.replace(/\s+/g, '-').toLowerCase()}-${mockup.id.slice(0, 8)}.png`;
      await downloadService.downloadFileFromUrl(getAssetUrl(mockup.url), filename, {
        useProxy: true,
        format: 'png',
      });
    } catch (error) {
      console.error('BrandCraft download failed:', error);
      alert('Failed to download mockup');
    }
  };

  /* ─── Render ─── */
  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="mx-auto max-w-5xl">

        {/* Hero Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-6 md:p-8 text-white mb-8">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE4YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 text-xs font-semibold tracking-wide uppercase">
              <Wand2 className="h-3.5 w-3.5" />
              AI Brand Builder
            </div>
            <h1 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">BrandCraft Studio</h1>
            <p className="mt-2 max-w-2xl text-sm text-white/75 leading-relaxed">
              Create a complete brand identity in minutes. Define your brand, choose your style, and let AI generate your logo and branded mockups.
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8 flex items-center justify-between">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.id}>
              <button
                onClick={() => i < currentStep && setCurrentStep(i)}
                disabled={i > currentStep}
                className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs font-semibold transition-all ${
                  i === currentStep
                    ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/25 scale-105'
                    : i < currentStep
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-pointer hover:bg-emerald-100'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {i < currentStep ? <CheckCircle2 className="h-4 w-4" /> : step.icon}
                <span className="hidden sm:inline">{step.label}</span>
              </button>
              {i < STEPS.length - 1 && (
                <ChevronRight className={`h-4 w-4 flex-shrink-0 ${i < currentStep ? 'text-emerald-400' : 'text-gray-300'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">

          {/* Step 1: Brand Identity */}
          {currentStep === 0 && (
            <div className="p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Tell us about your brand</h2>
                <p className="mt-1 text-sm text-gray-500">Start by defining the core identity of your brand.</p>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Brand Name *</label>
                  <input
                    type="text"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="e.g. Aurora Studio"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Tagline</label>
                  <input
                    type="text"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="e.g. Design that inspires"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Brand Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    placeholder="Describe what your brand does, who it serves, and what makes it unique..."
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 placeholder:text-gray-400 resize-none"
                  />
                </div>
              </div>

              {/* Live preview */}
              {brandName && (
                <div className="rounded-xl bg-gray-900 p-5 text-white">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-2">Preview</div>
                  <div className="text-2xl font-bold">{brandName}</div>
                  {tagline && <div className="mt-1 text-sm text-gray-400">{tagline}</div>}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Color Palette */}
          {currentStep === 1 && (
            <div className="p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Choose your color palette</h2>
                <p className="mt-1 text-sm text-gray-500">Select a curated palette or customize your own brand colors.</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {COLOR_PALETTES.map((palette, i) => {
                  const isSelected = selectedPaletteIdx === i;
                  return (
                    <button
                      key={i}
                      onClick={() => handleSelectPalette(i)}
                      className={`group rounded-xl border-2 p-3 text-left transition-all hover:shadow-md ${
                        isSelected ? 'border-violet-500 bg-violet-50 shadow-md ring-2 ring-violet-500/20' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex gap-1 mb-2">
                        <div className="h-8 w-8 rounded-lg" style={{ backgroundColor: palette.primary }} />
                        <div className="h-8 w-8 rounded-lg" style={{ backgroundColor: palette.secondary }} />
                        <div className="h-8 w-8 rounded-lg" style={{ backgroundColor: palette.accent }} />
                      </div>
                      <div className="text-xs font-semibold text-gray-900">{palette.name}</div>
                      <div className="text-[10px] text-gray-500">{palette.category}</div>
                      {isSelected && <CheckCircle2 className="h-4 w-4 text-violet-600 mt-1" />}
                    </button>
                  );
                })}
              </div>

              {/* Custom color inputs */}
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Or customize manually</div>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Primary', value: primaryColor, set: setPrimaryColor },
                    { label: 'Secondary', value: secondaryColor, set: setSecondaryColor },
                    { label: 'Accent', value: accentColor, set: setAccentColor },
                  ].map((c) => (
                    <div key={c.label}>
                      <label className="block text-[10px] font-medium text-gray-500 mb-1">{c.label}</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={c.value}
                          onChange={(e) => { c.set(e.target.value); setSelectedPaletteIdx(null); }}
                          className="h-9 w-9 rounded-lg border border-gray-200 cursor-pointer p-0"
                        />
                        <input
                          type="text"
                          value={c.value}
                          onChange={(e) => { c.set(e.target.value); setSelectedPaletteIdx(null); }}
                          className="flex-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs font-mono text-gray-700 uppercase"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Live color preview bar */}
              <div className="flex h-16 rounded-xl overflow-hidden shadow-inner">
                <div className="flex-[3]" style={{ backgroundColor: primaryColor }} />
                <div className="flex-[2]" style={{ backgroundColor: secondaryColor }} />
                <div className="flex-1" style={{ backgroundColor: accentColor }} />
              </div>
            </div>
          )}

          {/* Step 3: Typography */}
          {currentStep === 2 && (
            <div className="p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Select your typography</h2>
                <p className="mt-1 text-sm text-gray-500">Choose a font pairing that represents your brand's personality.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {FONT_PAIRINGS.map((pair, i) => {
                  const isSelected = selectedFontIdx === i;
                  return (
                    <button
                      key={i}
                      onClick={() => handleSelectFont(i)}
                      className={`group rounded-xl border-2 p-4 text-left transition-all hover:shadow-md ${
                        isSelected ? 'border-violet-500 bg-violet-50 shadow-md ring-2 ring-violet-500/20' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                          {pair.category}
                        </span>
                        {isSelected && <CheckCircle2 className="h-4 w-4 text-violet-600" />}
                      </div>
                      <div
                        className="text-lg font-bold text-gray-900 leading-tight"
                        style={{ fontFamily: `'${pair.heading}', sans-serif` }}
                      >
                        {brandName || 'Brand Name'}
                      </div>
                      <div
                        className="mt-1 text-sm text-gray-600 leading-relaxed"
                        style={{ fontFamily: `'${pair.body}', sans-serif` }}
                      >
                        {tagline || 'Your brand tagline goes here'}
                      </div>
                      <div className="mt-3 text-[10px] text-gray-500">
                        <span className="font-semibold">{pair.heading}</span> + <span>{pair.body}</span>
                      </div>
                      <div className="text-[10px] text-gray-400">{pair.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Logo Generation */}
          {currentStep === 3 && (
            <div className="p-6 md:p-8 space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Your AI-Generated Logos</h2>
                  <p className="mt-1 text-sm text-gray-500">Choose your favorite logo variation to use for your brand identity.</p>
                </div>
                {logos.length > 0 && (
                  <button
                    onClick={handleGenerateLogos}
                    disabled={generatingLogos}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${generatingLogos ? 'animate-spin' : ''}`} />
                    Regenerate
                  </button>
                )}
              </div>

              {generatingLogos && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="relative">
                    <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30 animate-pulse">
                      <Sparkles className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-gray-900">Generating Logo Variations</h3>
                  <p className="mt-2 text-sm text-gray-500 text-center max-w-md">
                    Our AI is crafting 4 unique logo styles for <strong>{brandName}</strong>. This usually takes 30-60 seconds...
                  </p>
                  <div className="mt-4 flex items-center gap-1.5">
                    {['minimal', 'wordmark', 'iconic', 'emblem'].map((s, i) => (
                      <span key={s} className="rounded-full bg-violet-100 px-2.5 py-1 text-[10px] font-semibold text-violet-700 animate-pulse" style={{ animationDelay: `${i * 200}ms` }}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {logoError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  {logoError}
                  <button onClick={handleGenerateLogos} className="mt-2 block text-xs font-medium text-red-800 underline">Try again</button>
                </div>
              )}

              {!generatingLogos && logos.length === 0 && !logoError && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-violet-500/20">
                    <Sparkles className="h-7 w-7" />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-gray-900">Ready to Generate</h3>
                  <p className="mt-2 text-sm text-gray-500 max-w-md">
                    Click the button below to generate 4 AI-powered logo variations for <strong>{brandName}</strong>.
                  </p>
                  <button
                    onClick={handleGenerateLogos}
                    className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  >
                    <Sparkles className="h-4 w-4" />
                    Generate Logos
                  </button>
                </div>
              )}

              {logos.length > 0 && !generatingLogos && (
                <>
                <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>Logos automatically saved to your <button type="button" onClick={() => window.location.href = '/dashboard/library'} className="font-semibold underline hover:text-emerald-900">Library</button></span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {logos.map((logo, i) => {
                    const isSelected = selectedLogoIdx === i;
                    return (
                      <button
                        key={logo.id}
                        onClick={() => setSelectedLogoIdx(i)}
                        className={`group relative rounded-2xl border-2 overflow-hidden transition-all hover:shadow-lg ${
                          isSelected ? 'border-violet-500 ring-4 ring-violet-500/20 shadow-lg' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="aspect-square bg-gray-50 p-4">
                          <img
                            src={getAssetUrl(logo.url)}
                            alt={`Logo variation: ${logo.style}`}
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <div className="p-3 border-t border-gray-100 bg-white">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-900 capitalize">{logo.style}</span>
                            {isSelected && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">
                                <Check className="h-3 w-3" /> Selected
                              </span>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-3 right-3">
                            <div className="h-6 w-6 rounded-full bg-violet-600 flex items-center justify-center shadow-lg">
                              <Check className="h-3.5 w-3.5 text-white" />
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                </>
              )}
            </div>
          )}

          {/* Step 5: Brand Preview */}
          {currentStep === 4 && (
            <div className="p-6 md:p-8 space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Your Brand Identity</h2>
                  <p className="mt-1 text-sm text-gray-500">Preview your complete brand with AI-generated mockups.</p>
                </div>
                {saved && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Brand Saved!
                  </span>
                )}
              </div>

              {/* Brand Summary Card */}
              <div className="rounded-xl overflow-hidden" style={{ backgroundColor: secondaryColor }}>
                <div className="p-6 flex items-center gap-5">
                  {selectedLogoIdx !== null && logos[selectedLogoIdx] && (
                    <div className="h-20 w-20 rounded-xl bg-white p-2 flex-shrink-0 shadow-lg">
                      <img src={getAssetUrl(logos[selectedLogoIdx].url)} alt="Logo" className="h-full w-full object-contain" />
                    </div>
                  )}
                  <div>
                    <div className="text-2xl font-bold" style={{ color: primaryColor, fontFamily: `'${fontHeading}', sans-serif` }}>
                      {brandName}
                    </div>
                    {tagline && (
                      <div className="mt-1 text-sm" style={{ color: accentColor, fontFamily: `'${fontBody}', sans-serif` }}>
                        {tagline}
                      </div>
                    )}
                    <div className="mt-2 flex gap-2">
                      {[primaryColor, secondaryColor, accentColor].map((c, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <div className="h-4 w-4 rounded border border-white/20" style={{ backgroundColor: c }} />
                          <span className="text-[10px] font-mono text-white/60">{c}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mockups Grid */}
              {generatingMockups && (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-10 w-10 animate-spin text-violet-600" />
                  <h3 className="mt-4 text-lg font-bold text-gray-900">Generating Brand Mockups</h3>
                  <p className="mt-1 text-sm text-gray-500">Creating branded business cards, stationery, social media kit, and signage...</p>
                </div>
              )}

              {mockups.length > 0 && (
                <>
                <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>Mockups automatically saved to your <button type="button" onClick={() => window.location.href = '/dashboard/library'} className="font-semibold underline hover:text-emerald-900">Library</button></span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {mockups.map((mockup) => (
                    <div key={mockup.id} className="group rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all">
                      <div className="aspect-[4/3] bg-gray-100">
                        <img
                          src={getAssetUrl(mockup.url)}
                          alt={mockup.templateName}
                          className="h-full w-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <div className="p-3 flex items-center justify-between bg-white">
                        <span className="text-xs font-semibold text-gray-900">{mockup.templateName}</span>
                        <button
                          type="button"
                          onClick={() => handleDownloadBrandMockup(mockup)}
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-violet-600 hover:text-violet-800"
                        >
                          <Download className="h-3 w-3" /> Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={handleSaveBrand}
                  disabled={saving || saved}
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <CheckCircle2 className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                  {saving ? 'Saving...' : saved ? 'Brand Saved!' : 'Save Brand Identity'}
                </button>
                {mockups.length > 0 && (
                  <button
                    onClick={handleGenerateMockups}
                    disabled={generatingMockups}
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 ${generatingMockups ? 'animate-spin' : ''}`} />
                    Regenerate Mockups
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Navigation Footer */}
          {currentStep < 4 && (
            <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 bg-gray-50/50">
              <button
                onClick={goBack}
                disabled={currentStep === 0}
                className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-white transition-colors disabled:opacity-30"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <div className="text-xs text-gray-400">
                Step {currentStep + 1} of {STEPS.length}
              </div>
              <button
                onClick={goNext}
                disabled={!canProceed() || generatingLogos}
                className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-violet-700 transition-colors disabled:opacity-40 disabled:hover:bg-violet-600"
              >
                {currentStep === 3 && logos.length === 0 ? (
                  <>
                    <Sparkles className="h-4 w-4" /> Generate Logos
                  </>
                ) : currentStep === 3 ? (
                  <>
                    Continue to Preview <ArrowRight className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    Next <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandCraft;
